import { inject, injectable } from "inversify";
import { IHttpService } from "../shared/httpService";
import TYPES from "@/ioc/identifiers";
import useGetProtocolHost from "@/use/useGetProtocolHost";
import { HttpError } from "../shared/errors/httpError";
import { Result, combine } from "neverthrow";
import { ParseError } from "../shared/errors/parseError";
import { ISystemCode } from "@/domain/entities/system";
import {
  HttpCustomCodeResponse,
  HttpSystemCodeResponse,
  ICustomCodeCollectionParser,
  ISystemCodeParser,
  ISystemRepository,
} from "./systemRepository.types";
import useGetAccessToken from "@/use/useGetAccessToken";
import { ICustomCodeCollection } from "@/domain/entities/system/customCode.types";
import { SERVICE_UID } from "@/types";

@injectable()
export class SystemRepository implements ISystemRepository {
  @inject(TYPES.ISystemCodeParser)
  private systemCodeParser!: ISystemCodeParser;

  @inject(TYPES.ICustomCodeCollectionParser)
  private customCodeCollectionParser!: ICustomCodeCollectionParser;

  private httpService: IHttpService;

  private isPublic = !useGetAccessToken();

  private publicPath = this.isPublic ? "/public" : "";

  constructor(@inject(TYPES.IHttpService) httpService: IHttpService) {
    this.httpService = httpService;
    this.httpService.initService(
      `${useGetProtocolHost()}/aibos/client/api/${
        process.env.VUE_APP_API_VERSION
      }${this.publicPath}/system`
    );
  }

  private getHeaders(
    isPublic: boolean,
    extraHeaders?: { [key: string]: string }
  ) {
    const token = useGetAccessToken();

    return isPublic
      ? extraHeaders
      : { ...extraHeaders, Authorization: `Bearer ${token}` };
  }

  async getSystemCodes(
    codeGroup?: string,
    parentCodes?: string
  ): Promise<Result<ISystemCode[], ParseError | HttpError>> {
    const parseTo = (response: HttpSystemCodeResponse) => {
      const systemCodes = response.data.map(this.systemCodeParser.toDomain);
      return combine(systemCodes);
    };
    return await this.httpService.get<HttpSystemCodeResponse, ISystemCode[]>(
      {
        url: "/code",
        config: {
          params: { codeGroup, parentCodes },
          headers: { Authorization: `Bearer ${useGetAccessToken()}` },
        },
      },
      { parseTo }
    );
  }

  async getCustomCodes(
    codeGroup?: string,
    parentCode?: string,
    parentUid?: string
  ): Promise<Result<ICustomCodeCollection, ParseError | HttpError>> {
    const parseTo = (response: HttpCustomCodeResponse) => {
      return this.customCodeCollectionParser.toDomain(response.data[0]);
    };

    return await this.httpService.get<
      HttpCustomCodeResponse,
      ICustomCodeCollection
    >(
      {
        url: "/custom-code",
        config: {
          params: { codeGroup, serviceUid: SERVICE_UID, parentCode, parentUid },
          headers: this.getHeaders(this.isPublic),
        },
      },
      { parseTo }
    );
  }
}
